// Hash-based router. Mirrors src/store.jsx's useHashRoute. Hash routing
// keeps the demo single-page-app friendly without needing react-router or
// next.js — and matches what the existing Babel-in-browser demo did.
//
// Routes:
//   #/                   → home
//   #/t/:threadId        → thread (default view: chat)
//   #/t/:threadId/brief  → same thread, focused on brief artifact
//   #/agents             → scheduled-agents panel
//   #/agents/:agentId    → agent detail
//   #/briefings          → briefings management list
//   #/briefings/:id      → briefing detail (with intake)

import { useEffect, useState } from "react";
import type { Route, RouteName } from "./types";

const KNOWN: ReadonlySet<RouteName> = new Set([
  "t",
  "agents",
  "briefings",
  "canvas",
  "brand",
  "graph",
  "training",
  "billing",
  "settings",
]);

function parseHash(hash: string): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const head = parts[0];
  const name: RouteName = (head && KNOWN.has(head as RouteName)
    ? (head as RouteName)
    : "home") as RouteName;
  return {
    raw: hash,
    name,
    threadId: name === "t" ? (parts[1] ?? null) : null,
    threadView:
      name === "t"
        ? ((parts[2] as "chat" | "brief" | undefined) ?? "chat")
        : null,
    agentId: name === "agents" ? (parts[1] ?? null) : null,
    briefingId: name === "briefings" ? (parts[1] ?? null) : null,
    canvasId: name === "canvas" ? (parts[1] ?? null) : null,
  };
}

export function useHashRoute(): {
  route: Route;
  navigate: (to: string) => void;
} {
  const [hash, setHash] = useState<string>(() => window.location.hash || "#/");

  useEffect(() => {
    const onChange = () => setHash(window.location.hash || "#/");
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  const navigate = (to: string) => {
    window.location.hash = to.startsWith("#") ? to : "#" + to;
  };

  return { route: parseHash(hash), navigate };
}
